/*
 * The group routes of protocol.md §3.2 "Groups", typed once.
 *
 * Nothing here holds state. A group's rows live in the client model — the bootstrap fills them, and
 * every mutation emits one `account.group.changed` carrying the whole group, which the reducer puts
 * in place of its copy (protocol.md §2.5.2) — so a screen calls one of the mutations and then renders
 * the model again. The one read is for pickers: the model holds the groups people browse and
 * mention, and the one-person groups every account has are fetched only by a screen that offers
 * them (`GroupPicker`). The other read is what a group's deletion would change, which only its
 * delete dialog asks.
 */

import { api } from "../net/api.ts"
import type { GroupView, UserId } from "../store/model.ts"

export interface CreateGroupInput {
	handle: string
	displayName: string
	mentionable: boolean
	memberIds?: UserId[]
}

/**
 * One line of what deleting a group would change, as the module that owns things through the group
 * answers it: a key of that module's catalogue, which takes the count as `{count}`.
 */
export interface GroupUse {
	key: string
	count: number
}

/** Every field optional; an absent one is left as it is. */
export interface UpdateGroupInput {
	handle?: string
	displayName?: string
	mentionable?: boolean
}

export const groupsApi = {
	/** Every group, the personal groups included: what a group picker chooses from. */
	listPickable(): Promise<GroupView[]> {
		return api
			.get<{ groups: GroupView[] }>("/api/groups?include=personal")
			.then((body) => body.groups)
	},

	create(input: CreateGroupInput): Promise<GroupView> {
		return api.post<{ group: GroupView }>("/api/groups", input).then((body) => body.group)
	},

	update(id: number, input: UpdateGroupInput): Promise<GroupView> {
		return api.patch<{ group: GroupView }>(`/api/groups/${id}`, input).then((body) => body.group)
	},

	/** What deleting the group would change: the lines its delete dialog shows, [] when nothing. */
	use(id: number): Promise<GroupUse[]> {
		return api.get<{ uses: GroupUse[] }>(`/api/groups/${id}/use`).then((body) => body.uses)
	},

	remove(id: number): Promise<void> {
		return api.del<void>(`/api/groups/${id}`)
	},

	addMembers(id: number, userIds: UserId[]): Promise<GroupView> {
		return api
			.post<{ group: GroupView }>(`/api/groups/${id}/members`, { userIds })
			.then((body) => body.group)
	},

	removeMember(id: number, userId: UserId): Promise<void> {
		return api.del<void>(`/api/groups/${id}/members/${userId}`)
	},
}
